'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  CreditCard,
  Calculator,
  Receipt,
  ChevronLeft,
  ChevronRight,
  Settings,
  Bell,
  User,
  Users,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const NAV_ITEMS = [
  { href: '/', label: 'Dashboard', icon: LayoutDashboard, badge: null },
  { href: '/lot-management', label: 'Lot Management', icon: Package, badge: '3' },
  { href: '/sales-management', label: 'Sales', icon: ShoppingCart, badge: null },
  { href: '/buyer-management', label: 'Buyers', icon: Users, badge: null },
  { href: '/payment-management', label: 'Payments', icon: CreditCard, badge: '2' },
  { href: '/settlement-module', label: 'Settlement', icon: Calculator, badge: '5' },
  { href: '/expense-management', label: 'Expenses', icon: Receipt, badge: null },
];

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();

  return (
    <aside
      className="fixed left-0 top-0 h-full bg-card border-r border-border flex flex-col z-40 transition-all duration-300"
      style={{ width: collapsed ? 'var(--sidebar-collapsed)' : 'var(--sidebar-width)' }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-4 border-b border-border h-14">
        <AppLogo size={28} />
        {!collapsed && (
          <div className="flex flex-col overflow-hidden">
            <span className="text-sm font-bold text-foreground leading-tight truncate">MarketLot</span>
            <span className="text-2xs text-muted-foreground truncate">ERP v1.0</span>
          </div>
        )}
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-2 py-3 space-y-1 overflow-y-auto scrollbar-none">
        {!collapsed && (
          <p className="section-header px-2 pb-2">Main Menu</p>
        )}
        {NAV_ITEMS.map((item) => {
          const isActive = pathname === item.href;
          const Icon = item.icon;
          return (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 group relative ${
                isActive ? 'nav-item-active' : 'nav-item-inactive'
              }`}
              title={collapsed ? item.label : undefined}
            >
              <Icon size={18} className={isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'} />
              {!collapsed && (
                <span className={`text-sm font-medium flex-1 ${isActive ? 'text-primary' : ''}`}>
                  {item.label}
                </span>
              )}
              {!collapsed && item.badge && (
                <span className="bg-primary text-primary-foreground text-2xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {item.badge}
                </span>
              )}
              {collapsed && item.badge && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-border px-2 py-3 space-y-1">
        <Link href="#" className="nav-item-inactive flex items-center gap-3 px-3 py-2.5">
          <Bell size={18} />
          {!collapsed && <span className="text-sm font-medium">Alerts</span>}
        </Link>
        <Link href="#" className="nav-item-inactive flex items-center gap-3 px-3 py-2.5">
          <Settings size={18} />
          {!collapsed && <span className="text-sm font-medium">Settings</span>}
        </Link>
        {/* User */}
        <div className="flex items-center gap-3 px-3 py-2.5 mt-2 border-t border-border pt-3">
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <User size={14} className="text-primary" />
          </div>
          {!collapsed && (
            <div className="flex-1 overflow-hidden">
              <p className="text-xs font-semibold text-foreground truncate">Murugan & Sons</p>
              <p className="text-2xs text-muted-foreground truncate">Admin</p>
            </div>
          )}
        </div>
      </div>

      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center hover:bg-muted transition-colors z-50"
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}